/* global Office */
import { useState, useEffect, useRef, useCallback } from "react";
import {
  FluentProvider,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import { EditRegular, TranslateRegular } from "@fluentui/react-icons";

const useTabStyles = makeStyles({
  tab: {
    // Selected indicator (the animated underline) -> accent. Fluent uses
    // `:enabled:hover/active::after` so we must match that specificity to win.
    "::after": { backgroundColor: "var(--aco-accent)" },
    ":enabled:hover::after": { backgroundColor: "var(--aco-accent)" },
    ":enabled:active::after": { backgroundColor: "var(--aco-accent)" },
    // Hover/pending indicator line (a separate `::before`) -> accent, not gray.
    ":hover::before": { backgroundColor: "var(--aco-accent)" },
    ":active::before": { backgroundColor: "var(--aco-accent)" },
    // Selected label + icon -> accent (rest + hover).
    "&[aria-selected=true]": { color: "var(--aco-accent)" },
    "&[aria-selected=true]:enabled:hover": { color: "var(--aco-accent)" },
    "&[aria-selected=true] .fui-Tab__icon": { color: "var(--aco-accent)" },
    "&[aria-selected=true]:enabled:hover .fui-Tab__icon": { color: "var(--aco-accent)" },
  },
});

import TRANSLATIONS from "./constants/translations";
import { formatTextToHTML } from "./utils/format";
import {
  getDraftRecipient,
  getEmailBody,
  getEmailBodyWithoutSignature,
  getEmailSubject,
  getUserEmail,
  insertIntoOutlook,
  isComposeMode,
} from "./utils/office";
import { extractNER, streamChatDraft, summarize, translate } from "./utils/api";
import { loadPrefs, savePrefs } from "./utils/prefs";
import { useOfficeDarkMode } from "./hooks/useOfficeDarkMode";

import AnalysisCard from "./components/AnalysisCard";
import ChatPanel from "./components/ChatPanel";
import Header from "./components/Header";
import TranslationCard from "./components/TranslationCard";

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

let messageIdCounter = 0;
const nextId = () => `m${++messageIdCounter}`;

export default function App() {
  const isDark = useOfficeDarkMode();
  const tabStyles = useTabStyles();

  const [uiLang, setUiLang] = useState(() => loadPrefs().uiLang);
  const [replyLang, setReplyLang] = useState(() => loadPrefs().replyLang);
  const [tone, setTone] = useState(() => loadPrefs().tone);
  const [length, setLength] = useState(() => loadPrefs().length);
  const [accentColor, setAccentColor] = useState(() => loadPrefs().accentColor);

  const isFirstPrefsRender = useRef(true);
  useEffect(() => {
    if (isFirstPrefsRender.current) {
      isFirstPrefsRender.current = false;
      return;
    }
    savePrefs({ uiLang, replyLang, tone, length, accentColor });
  }, [uiLang, replyLang, tone, length, accentColor]);

  const [userEmail, setUserEmail] = useState("");
  const [currentEmail, setCurrentEmail] = useState({
    sender: "",
    senderEmail: "",
    subject: "",
    recipient: "",
    mode: "read",
  });

  const isSelfSender =
    !!userEmail &&
    !!currentEmail.senderEmail &&
    userEmail.trim().toLowerCase() === currentEmail.senderEmail.trim().toLowerCase();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [nerText, setNerText] = useState(null);
  const [analysisOpen, setAnalysisOpen] = useState(true);

  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef(null);
  // In-flight analysis request (summary + NER), aborted when the email changes.
  const analysisAbortRef = useRef(null);

  // Active view: "main" (analysis + chat) or "translate".
  const [view, setView] = useState("main");
  const [translateLang, setTranslateLang] = useState(() => loadPrefs().uiLang);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [translateEmpty, setTranslateEmpty] = useState(false);
  const translateReqRef = useRef(0);
  // In-flight translation request, aborted when the target language changes.
  const translateAbortRef = useRef(null);
  // Language the cached translation corresponds to (null => nothing cached).
  const translatedLangRef = useRef(null);

  const t = useCallback(
    (key) => TRANSLATIONS[uiLang]?.[key] ?? TRANSLATIONS["fr"][key] ?? key,
    [uiLang]
  );

  useEffect(() => {
    setUserEmail(getUserEmail());

    const updateEmailInfo = () => {
      const item = window.Office?.context?.mailbox?.item;
      if (!item) return;

      if (isComposeMode()) {
        // Draft / compose: no sender.
        // Resolve the recipient asynchronously for a meaningful header context.
        getDraftRecipient().then((recipient) => {
          setCurrentEmail({
            sender: "",
            senderEmail: "",
            subject: "",
            recipient,
            mode: "compose",
          });
        });
      } else {
        setCurrentEmail({
          sender: item.from ? item.from.displayName : "",
          senderEmail: item.from ? item.from.emailAddress : "",
          subject: item.subject || "",
          recipient: "",
          mode: "read",
        });
      }

      // New email/draft selected -> drop any in-flight analysis/translation
      // requests for the previous email, back to main view, reset chat + cache.
      analysisAbortRef.current?.abort();
      translateAbortRef.current?.abort();
      setMessages([]);
      setAnalysisOpen(true);
      setView("main");
      setTranslatedText(null);
      setTranslateError(false);
      setTranslateEmpty(false);
      translatedLangRef.current = null;
    };

    updateEmailInfo();

    if (window.Office && Office.context && Office.context.mailbox) {
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, updateEmailInfo);
    }

    return () => {
      if (window.Office && Office.context && Office.context.mailbox) {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, updateEmailInfo);
      }
    };
  }, []);

  const debouncedAutoAnalysis = useCallback(
    debounce((language, email) => {
      if (!email) return;
      runAnalysis(language, email);
    }, 300),
    []
  );

  useEffect(() => {
    if (currentEmail.mode === "compose") {
      setSummaryText(null);
      setNerText(null);
      return;
    }
    debouncedAutoAnalysis(uiLang, userEmail);
  }, [uiLang, userEmail, currentEmail]);

  async function runAnalysis(language, email) {
    analysisAbortRef.current?.abort();
    const controller = new AbortController();
    analysisAbortRef.current = controller;

    setIsAnalyzing(true);
    setAnalysisError(false);
    setSummaryText(null);
    setNerText(null);

    const isCurrent = () => analysisAbortRef.current === controller;

    try {
      const [body, subject] = await Promise.all([getEmailBody(), getEmailSubject()]);

      if (!body.trim() && !subject.trim()) {
        setSummaryText(null);
        setNerText(null);
        return;
      }

      await Promise.all([
        summarize({
          subject,
          body,
          language,
          tone: "professional",
          length: "short",
          userEmail: email,
          signal: controller.signal,
          onChunk: (_delta, accumulated) => {
            if (isCurrent()) setSummaryText(accumulated);
          },
        }),
        extractNER({
          subject,
          body,
          language,
          tone: "professional",
          length: "short",
          userEmail: email,
          signal: controller.signal,
          onChunk: (_delta, accumulated) => {
            if (isCurrent()) setNerText(accumulated);
          },
        }),
      ]);
    } catch (err) {
      if (err?.name === "AbortError") return;
      setAnalysisError(true);
    } finally {
      // Only the latest analysis owns the loading flag.
      // If a newer request has replaced this one, leave the flag for it;
      // otherwise clear it.
      if (analysisAbortRef.current === controller) {
        analysisAbortRef.current = null;
        setIsAnalyzing(false);
      }
    }
  }

  async function runTranslation(targetLang) {
    translateAbortRef.current?.abort();
    const controller = new AbortController();
    translateAbortRef.current = controller;

    const reqId = ++translateReqRef.current;
    setIsTranslating(true);
    setTranslateError(false);
    setTranslateEmpty(false);
    setTranslatedText(null);
    translatedLangRef.current = null;

    try {
      const [body, subject] = await Promise.all([
        getEmailBodyWithoutSignature(),
        getEmailSubject(),
      ]);

      if (!body.trim() && !subject.trim()) {
        if (reqId === translateReqRef.current) {
          setTranslatedText(null);
          setTranslateEmpty(true);
        }
        return;
      }

      const result = await translate({
        subject,
        body,
        targetLanguage: targetLang,
        userEmail,
        signal: controller.signal,
        onChunk: (_delta, accumulated) => {
          if (reqId === translateReqRef.current) setTranslatedText(accumulated);
        },
      });
      if (reqId === translateReqRef.current) {
        setTranslatedText(result);
        translatedLangRef.current = targetLang;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (reqId === translateReqRef.current) setTranslateError(true);
    } finally {
      if (translateAbortRef.current === controller) translateAbortRef.current = null;
      if (reqId === translateReqRef.current) setIsTranslating(false);
    }
  }

  useEffect(() => {
    if (view !== "translate" || currentEmail.mode === "compose" || isSelfSender) return;
    if (translatedLangRef.current === translateLang) return;
    runTranslation(translateLang);
    // Leaving the translate view (or unmount) drops the in-flight translation.
    return () => translateAbortRef.current?.abort();
  }, [view, translateLang, currentEmail, userEmail]);

  async function handleSendMessage(text) {
    if (isStreaming || isSelfSender) return;

    setAnalysisOpen(false);

    const userMsg = { id: nextId(), role: "user", content: text };
    const assistantId = nextId();
    const assistantMsg = { id: assistantId, role: "assistant", content: "", error: false };

    const historyWithUser = [...messages, userMsg];
    setMessages([...historyWithUser, assistantMsg]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const [body, subject] = await Promise.all([getEmailBody(), getEmailSubject()]);

      const isCompose = currentEmail.mode === "compose";
      const payload = {
        subject,
        body,
        sender: currentEmail.sender || null,
        receiver: (isCompose ? currentEmail.recipient : userEmail) || null,
        language: replyLang,
        tone,
        length,
        mode: isCompose ? "compose" : "reply",
        messages: historyWithUser.map((m) => ({ role: m.role, content: m.content })),
      };

      await streamChatDraft({
        payload,
        userEmail,
        signal: controller.signal,
        onChunk: (_chunk, accumulated) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: accumulated } : m))
          );
        },
      });
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: "", error: true, errorStatus: err.status } : m
        )
      );
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }

  async function handleUseAsMail(plainText) {
    if (!plainText) return;
    const html = formatTextToHTML(plainText);
    try {
      await insertIntoOutlook(html);
    } catch {
      // silently ignored, Outlook surface error is rare and recoverable
    }
  }

  const theme = {
    ...(isDark ? webDarkTheme : webLightTheme),
    colorCompoundBrandStroke: accentColor,
    colorCompoundBrandStrokeHover: accentColor,
    colorCompoundBrandStrokePressed: accentColor,
  };

  return (
    <FluentProvider
      theme={theme}
      style={{
        height: "100vh",
        fontFamily: "'Segoe UI', sans-serif",
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        "--aco-accent": accentColor,
        "--aco-accent-hover": `color-mix(in srgb, ${accentColor} 85%, black)`,
        "--aco-accent-active": `color-mix(in srgb, ${accentColor} 75%, black)`,
        "--aco-accent-bg": `color-mix(in srgb, ${accentColor} 8%, transparent)`,
        "--aco-accent-border": `color-mix(in srgb, ${accentColor} 25%, transparent)`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: "4px 6px 6px 6px",
          gap: "6px",
          boxSizing: "border-box",
        }}
      >
        <Header
          uiLang={uiLang}
          setUiLang={setUiLang}
          accentColor={accentColor}
          setAccentColor={setAccentColor}
          currentEmail={currentEmail}
          t={t}
        />

        {currentEmail.mode !== "compose" && !isSelfSender && (
          <TabList selectedValue={view} onTabSelect={(_, d) => setView(d.value)} size="small">
            <Tab value="main" icon={<EditRegular />} className={tabStyles.tab}>
              {t("tab.edit")}
            </Tab>
            <Tab value="translate" icon={<TranslateRegular />} className={tabStyles.tab}>
              {t("tab.translate")}
            </Tab>
          </TabList>
        )}

        {view === "translate" && currentEmail.mode !== "compose" && !isSelfSender ? (
          <TranslationCard
            translateLang={translateLang}
            setTranslateLang={setTranslateLang}
            translatedText={translatedText}
            isTranslating={isTranslating}
            isError={translateError}
            isEmpty={translateEmpty}
            onUseAsMail={handleUseAsMail}
            t={t}
          />
        ) : (
          <>
            {currentEmail.mode !== "compose" && (
              <AnalysisCard
                summaryText={summaryText}
                nerText={nerText}
                isAnalyzing={isAnalyzing}
                isError={analysisError}
                isOpen={analysisOpen}
                onToggle={setAnalysisOpen}
                t={t}
              />
            )}

            {isSelfSender ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                  textAlign: "center",
                }}
              >
                <Text
                  size={300}
                  style={{ color: tokens.colorNeutralForeground3, fontStyle: "italic" }}
                >
                  {t("status.impossible")}
                </Text>
              </div>
            ) : (
              <ChatPanel
                messages={messages}
                isStreaming={isStreaming}
                canSend={!!userEmail && !isSelfSender}
                onSend={handleSendMessage}
                onUseAsMail={handleUseAsMail}
                replyLang={replyLang}
                setReplyLang={setReplyLang}
                tone={tone}
                setTone={setTone}
                length={length}
                setLength={setLength}
                t={t}
              />
            )}
          </>
        )}
      </div>
    </FluentProvider>
  );
}
